import {
  DemandFragment,
  DemandType,
  HourSeriesType,
  PurchasedEnergyType,
} from '../generated/graphql'
import { reduceToSumOfItems, subtractItems } from '../util/array-utility'
import type { HourlyEnergyFlow } from './HourlyEnergyFlow'

export enum EnergyType {
  Heating = 'heating',
  Cooling = 'cooling',
  Electricity = 'electricity',
}

export enum WasteType {
  Lost = 'lost',
  Sold = 'sold',
  Recycled = 'recycled',
}

export enum Quadrant {
  Waste = 'waste',
  Provided = 'provided',
  Requirement = 'requirement',
  FromEnvironment = 'environment',
}

export type Energy = {
  energyType: EnergyType
  hourSeries: number[]
}

/** Primary energy flows directly related to demands */
export type PrimaryEnergyFlow = Energy & {
  quadrant: Quadrant
  wasteType?: WasteType
  demandId: string
}

/** Secondary energy flows created in providers and not directly related to demands */
export type SecondaryEnergyFlow = Omit<PrimaryEnergyFlow, 'demandId'> & {
  providerId: string
}

export type EnergyFlow = PrimaryEnergyFlow | SecondaryEnergyFlow

export function filterIncoming(flow: EnergyFlow): boolean {
  return (
    flow.quadrant === Quadrant.Requirement ||
    flow.quadrant === Quadrant.FromEnvironment
  )
}

export function filterOutgoing(flow: EnergyFlow): boolean {
  return flow.quadrant === Quadrant.Provided || flow.quadrant === Quadrant.Waste
}

export function checkForInvalid<T extends EnergyFlow>(
  flow: T | null,
): flow is T {
  if (!flow) {
    throw new Error('Please check calculation order')
  }
  return true
}

export const isPrimaryEnergyFlow = (
  flow: EnergyFlow,
): flow is PrimaryEnergyFlow => Boolean((flow as PrimaryEnergyFlow).demandId)

export const isSecondaryEnergyFlow = (
  flow: EnergyFlow,
): flow is SecondaryEnergyFlow =>
  Boolean((flow as SecondaryEnergyFlow).providerId)

const createEnergyFlow =
  (quadrant: Quadrant, wasteType?: WasteType) =>
  (
    demandId: string,
    energyType: EnergyType,
    hourSeries: number[],
  ): PrimaryEnergyFlow => ({
    demandId,
    quadrant,
    hourSeries,
    energyType,
    wasteType,
  })

export const createWaste = (
  demandId: string,
  energyType: EnergyType,
  hourSeries: number[],
  wasteType = WasteType.Lost,
): PrimaryEnergyFlow =>
  createEnergyFlow(Quadrant.Waste, wasteType)(demandId, energyType, hourSeries)
export const createProvided = createEnergyFlow(Quadrant.Provided)
export const createFromEnvironment = createEnergyFlow(Quadrant.FromEnvironment)
export const createRequirement = createEnergyFlow(Quadrant.Requirement)

const createSecondaryEnergyFlow =
  (quadrant: Quadrant, wasteType?: WasteType) =>
  (
    providerId: string,
    energyType: EnergyType,
    hourSeries: number[],
  ): SecondaryEnergyFlow => ({
    providerId,
    quadrant,
    hourSeries,
    energyType,
    wasteType,
  })

export const createSecondaryWaste = (
  providerId: string,
  energyType: EnergyType,
  hourSeries: number[],
  wasteType = WasteType.Lost,
): SecondaryEnergyFlow =>
  createSecondaryEnergyFlow(Quadrant.Waste, wasteType)(
    providerId,
    energyType,
    hourSeries,
  )
export const createSecondaryProvided = createSecondaryEnergyFlow(
  Quadrant.Provided,
)
export const createSecondaryFromEnvironment = createSecondaryEnergyFlow(
  Quadrant.FromEnvironment,
)
export const createSecondaryRequirement = createSecondaryEnergyFlow(
  Quadrant.Requirement,
)

const createNewEnergyFlowFromExisting =
  (quadrant: Quadrant) =>
  (
    energyFlow: EnergyFlow,
    energyType: EnergyType,
    hourSeries: number[],
  ): EnergyFlow => {
    if (isPrimaryEnergyFlow(energyFlow)) {
      return createEnergyFlow(quadrant)(
        energyFlow.demandId,
        energyType,
        hourSeries,
      )
    }

    if (isSecondaryEnergyFlow(energyFlow)) {
      return createSecondaryEnergyFlow(quadrant)(
        energyFlow.providerId,
        energyType,
        hourSeries,
      )
    }

    throw new Error('Error creating energy flow')
  }

export const createWasteFrom = createNewEnergyFlowFromExisting(Quadrant.Waste)
export const createDemandFrom = createNewEnergyFlowFromExisting(
  Quadrant.Provided,
)
export const createInternalFrom = createNewEnergyFlowFromExisting(
  Quadrant.FromEnvironment,
)
export const createExternalFrom = createNewEnergyFlowFromExisting(
  Quadrant.Requirement,
)

/**
 * Creates a function to check if primary flows are equal. Energy type must
 * also match as transferred demands can have the same demand id.
 */
export function isPrimaryEnergyFlowEqual(
  demandId: string,
  energyType: EnergyType,
) {
  return (originalFlow: PrimaryEnergyFlow): boolean =>
    originalFlow.demandId === demandId && originalFlow.energyType === energyType
}

/**
 * Creates a function to check if secondary flows are equal. Energy type must
 * also match as transferred demands can have the same provider id.
 */
export function isSecondaryEnergyFlowEqual(
  providerId: string,
  energyType: EnergyType,
) {
  return (originalFlow: SecondaryEnergyFlow): boolean =>
    originalFlow.providerId === providerId &&
    originalFlow.energyType === energyType
}

export function createMapToRemainingDemand<T extends EnergyFlow>(
  providedEnergyFlows: EnergyFlow[],
) {
  return (initialEnergyFlow: T): T => {
    let providedHourSeries: number[] = []

    if (isPrimaryEnergyFlow(initialEnergyFlow)) {
      providedHourSeries =
        providedEnergyFlows
          .filter(isPrimaryEnergyFlow)
          .find(
            isPrimaryEnergyFlowEqual(
              initialEnergyFlow.demandId,
              initialEnergyFlow.energyType,
            ),
          )?.hourSeries ?? []
    } else if (isSecondaryEnergyFlow(initialEnergyFlow)) {
      providedHourSeries =
        providedEnergyFlows
          .filter(isSecondaryEnergyFlow)
          .find(
            isSecondaryEnergyFlowEqual(
              initialEnergyFlow.providerId,
              initialEnergyFlow.energyType,
            ),
          )?.hourSeries ?? []
    } else {
      throw new Error('Error while creating map to remaining demand')
    }

    const remainingDemandHourSeries = subtractItems(
      initialEnergyFlow.hourSeries,
      providedHourSeries,
    )

    return {
      ...initialEnergyFlow,
      hourSeries: remainingDemandHourSeries,
    }
  }
}

const demandTypeToEnergyTypeMap = new Map<DemandType, EnergyType>([
  [DemandType.Heating, EnergyType.Heating],
  [DemandType.Cooling, EnergyType.Cooling],
  [DemandType.Electricity, EnergyType.Electricity],
])
const energyTypeToDemandTypeMap = new Map(
  Array.from(demandTypeToEnergyTypeMap, (entry) => [entry[1], entry[0]]),
)

export function mapDemandTypeToEnergyType(demandType: DemandType): EnergyType {
  const energyType = demandTypeToEnergyTypeMap.get(demandType)

  if (!energyType) {
    throw new Error(`Unable to map '${demandType}' to energy type`)
  }

  return energyType
}

export function mapEnergyTypeToDemandType(energyType: EnergyType): DemandType {
  const demandType = energyTypeToDemandTypeMap.get(energyType)

  if (!demandType) {
    throw new Error(`Unable to map '${energyType}' to demand type`)
  }

  return demandType
}

export function mapDemandToEnergyFlow(
  demand: DemandFragment,
): PrimaryEnergyFlow {
  const energyType = mapDemandTypeToEnergyType(demand.type)

  return createProvided(demand.id, energyType, demand.hourSeries.values)
}

export const purhasedEnergyTypeToEnergyTypeMap = new Map<
  PurchasedEnergyType,
  EnergyType
>([
  [PurchasedEnergyType.Heating, EnergyType.Heating],
  [PurchasedEnergyType.Cooling, EnergyType.Cooling],
  [PurchasedEnergyType.Electricity, EnergyType.Electricity],
])

const hourSeriesTypeToEnergyTypeMap = new Map<HourSeriesType, EnergyType>([
  [HourSeriesType.HeatingDemand, EnergyType.Heating],
  [HourSeriesType.CoolingDemand, EnergyType.Cooling],
  [HourSeriesType.ElectricityDemand, EnergyType.Electricity],
  [HourSeriesType.HeatingDemandDensity, EnergyType.Heating],
  [HourSeriesType.CoolingDemandDensity, EnergyType.Cooling],
  [HourSeriesType.ElectricityDemandDensity, EnergyType.Electricity],
])

export function mapHourSeriesTypeToEnergyType(
  hourSeriesType: HourSeriesType,
): EnergyType {
  const energyType = hourSeriesTypeToEnergyTypeMap.get(hourSeriesType)

  if (!energyType) {
    throw new Error(`Unable to map '${hourSeriesType}' to energy type`)
  }

  return energyType
}

export const createCombinedInputFlows = (
  inputEnergyFlowsByEnergyType: Record<string, EnergyFlow[]>,
): PrimaryEnergyFlow[] =>
  Object.entries(inputEnergyFlowsByEnergyType)
    .map(([energyType, energyTypeFlows]) =>
      createProvided(
        energyType,
        energyType as EnergyType,
        energyTypeFlows
          .map((flow) => flow.hourSeries)
          .reduce(reduceToSumOfItems, []),
      ),
    )
    .filter((flow) => flow.hourSeries.length > 0)

export function isRequirement(flow: EnergyFlow | HourlyEnergyFlow): boolean {
  return flow.quadrant === Quadrant.Requirement
}
export function isProvided(flow: EnergyFlow | HourlyEnergyFlow): boolean {
  return flow.quadrant === Quadrant.Provided
}
export function isFromEnvironment(
  flow: EnergyFlow | HourlyEnergyFlow,
): boolean {
  return flow.quadrant === Quadrant.FromEnvironment
}

export function isWaste(flow: EnergyFlow | HourlyEnergyFlow): boolean {
  return flow.quadrant === Quadrant.Waste
}
